import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getPersonaVariant, personaCopy, PersonaVariant } from './data/personaCopy';
import { WaitlistFormData } from './types/waitlist';
import { ProgressiveDisclosure, ExitIntentDetector } from './utils/behavioralScience';
import HeroSection from './components/HeroSection';
import VideoDemo from './components/VideoDemo';
import ProgressIndicator from './components/ProgressIndicator';
import ExitIntentPopup from './components/ExitIntentPopup';
import SuccessState from './components/SuccessState';
import ConfirmedEmailPage from './components/ConfirmedEmailPage';
import Footer from './components/Footer';
import TermsPage from './components/TermsPage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import ReferralCodePolicyPage from './components/ReferralCodePolicyPage';
import QuestionsPage from './components/QuestionsPage'; // Added import for QuestionsPage
import { waitlistAPI } from './services/waitlistApi';
import './App.css';

// Main App Component with Router
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

// Main App Content Component
function AppContent() {
  const [personaVariant, setPersonaVariant] = useState<PersonaVariant>('problem_solution');
  const [formData, setFormData] = useState<WaitlistFormData>({
    email: '',
    keepUpdated: true,
  });
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showQuestions, setShowQuestions] = useState(false);
  const [showExitIntent, setShowExitIntent] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfirmedEmail, setShowConfirmedEmail] = useState(false);
  const [activeTab, setActiveTab] = useState<'waitlist' | 'mission'>('waitlist');
  const [verificationToken, setVerificationToken] = useState<string>('');
  const [referralData, setReferralData] = useState<any>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const copy = React.useMemo(() => personaCopy[personaVariant], [personaVariant]);

  useEffect(() => {
    // Determine persona variant on component mount
    const variant = getPersonaVariant();
    setPersonaVariant(variant);

    // Start exit-intent detection
    // const cleanup = ExitIntentDetector.startDetection(() => {
    //   setShowExitIntent(true);
    // });

    // return () => {
    //   return cleanup;
    // };
  }, []);

  const handleWaitlistSubmit = async (email: string, keepUpdated: boolean, questionsData?: any) => {
    setIsLoading(true);
    setApiError(null);

    try {
      // Update form data
      setFormData(prev => ({ ...prev, email, keepUpdated, ...questionsData }));
      
      // Extract referral data if available
      if (questionsData?.referralData) {
        setReferralData(questionsData.referralData);
      }
      
      setCompletedSteps(prev => [...prev, 'email', 'questions']);
      setShowSuccess(true);
    } catch (error) {
      console.error('Waitlist submission failed:', error);
      setApiError(error instanceof Error ? error.message : 'Submission failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExitIntentClose = () => {
    setShowExitIntent(false);
    ExitIntentDetector.reset();
  };

  const handleExitIntentSubmit = () => {
    setShowExitIntent(false);
    ExitIntentDetector.reset();
  };

  const currentStep = ProgressiveDisclosure.getCurrentStep(completedSteps);
  const progressPercentage = ProgressiveDisclosure.getProgressPercentage(completedSteps);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 via-primary-800 to-secondary-900 relative no-overscroll">
      {/* Main Content */}
      <div className="relative z-10">
        <Routes>
          {/* Privacy Policy Route */}
          <Route 
            path="/privacy" 
            element={
              <PrivacyPolicyPage 
                onBack={() => window.history.back()} 
              />
            } 
          />

          {/* Terms & Conditions Route */}
          <Route 
            path="/terms" 
            element={
              <TermsPage 
                onBack={() => window.history.back()} 
              />
            } 
          />

          {/* Referral Code Policy Route */}
          <Route 
            path="/referral-policy" 
            element={
              <ReferralCodePolicyPage 
                onBack={() => window.history.back()} 
              />
            } 
          />

          {/* Questions Route */}
          <Route 
            path="/questions" 
            element={
              <QuestionsPage 
                onTermsClick={() => window.location.href = '/terms'}
                onPrivacyClick={() => window.location.href = '/privacy'}
                onBack={() => window.history.back()} 
              />
            } 
          />

          {/* Email Confirmation Route */}
          <Route 
            path="/confirm" 
            element={
              <ConfirmedEmailPage 
                onTermsClick={() => window.location.href = '/terms'}
                onPrivacyClick={() => window.location.href = '/privacy'}
                token={new URLSearchParams(window.location.search).get('token') || ''}
              />
            } 
          />

          {/* Main App Route */}
          <Route 
            path="/" 
            element={
              <MainAppContent
                copy={copy}
                onSubmit={handleWaitlistSubmit}
                isLoading={isLoading}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                showExitIntent={showExitIntent}
                onExitIntentClose={handleExitIntentClose}
                onExitIntentSubmit={handleExitIntentSubmit}
                showSuccess={showSuccess}
                setShowSuccess={setShowSuccess}
                referralData={referralData}
                setReferralData={setReferralData}
                showConfirmedEmail={showConfirmedEmail}
                setShowConfirmedEmail={setShowConfirmedEmail}
                verificationToken={verificationToken}
                setVerificationToken={setVerificationToken}
                apiError={apiError}
                setApiError={setApiError}
                showQuestions={showQuestions}
                setShowQuestions={setShowQuestions}
                completedSteps={completedSteps}
                currentStep={currentStep}
                progressPercentage={progressPercentage}
                formData={formData}
              />
            } 
          />

          {/* Catch all route - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}

// Main App Content Component
function MainAppContent({
  copy,
  onSubmit,
  isLoading,
  activeTab,
  setActiveTab,
  showExitIntent,
  onExitIntentClose,
  onExitIntentSubmit,
  showSuccess,
  setShowSuccess,
  referralData,
  setReferralData,
  showConfirmedEmail,
  setShowConfirmedEmail,
  verificationToken,
  setVerificationToken,
  apiError,
  setApiError,
  showQuestions,
  setShowQuestions,
  completedSteps,
  currentStep,
  progressPercentage,
  formData
}: {
  copy: any;
  onSubmit: (email: string, keepUpdated: boolean, questionsData?: any) => Promise<void>;
  isLoading: boolean;
  activeTab: 'waitlist' | 'mission';
  setActiveTab: (tab: 'waitlist' | 'mission') => void;
  showExitIntent: boolean;
  onExitIntentClose: () => void;
  onExitIntentSubmit: () => void;
  showSuccess: boolean;
  setShowSuccess: (show: boolean) => void;
  referralData: any;
  setReferralData: (data: any) => void;
  showConfirmedEmail: boolean;
  setShowConfirmedEmail: (show: boolean) => void;
  verificationToken: string;
  setVerificationToken: (token: string) => void;
  apiError: string | null;
  setApiError: (error: string | null) => void;
  showQuestions: boolean;
  setShowQuestions: (show: boolean) => void;
  completedSteps: string[];
  currentStep: string;
  progressPercentage: number;
  formData: WaitlistFormData;
}) {
  const navigate = useNavigate();

  const handleTermsClick = () => navigate('/terms');
  const handlePrivacyClick = () => navigate('/privacy');

  return (
    <>
      <AnimatePresence mode="wait">
        {showConfirmedEmail ? (
          <ConfirmedEmailPage
            key="confirmed-email"
            onTermsClick={handleTermsClick}
            onPrivacyClick={handlePrivacyClick}
            token={verificationToken}
          />
        ) : showSuccess ? (
          <SuccessState
            key="success"
            copy={copy}
            onShare={() => {
              // Handle share functionality
              console.log('Share clicked');
            }}
            onTermsClick={handleTermsClick}
            onPrivacyClick={handlePrivacyClick}
            referralData={referralData}
          />
        ) : (
          <motion.div
            key="main"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Header */}
            <header className="relative z-10 pt-8">
              <div className="relative flex flex-col items-center space-y-6">
                {/* Login Button - Top Right */}
                <div className="absolute top-0 right-4 sm:right-8">
                  <a
                    href="https://app.quellect.ai/login"
                    className="inline-flex items-center justify-center px-4 py-2 sm:px-6 sm:py-2 text-sm sm:text-base font-medium text-gray-800 bg-white rounded-full border border-gray-200 shadow-lg hover:bg-gray-50 transition-all duration-200"
                  >
                    Login
                  </a>
                </div>

                {/* Navigation Pill */}
                <div className="nav-pill">
                  <button
                    onClick={() => setActiveTab('waitlist')}
                    className={`nav-segment ${activeTab === 'waitlist' ? 'nav-segment-active' : 'nav-segment-inactive'}`}
                  >
                    Waitlist
                  </button>
                  <button
                    onClick={() => setActiveTab('mission')}
                    className={`nav-segment ${activeTab === 'mission' ? 'nav-segment-active' : 'nav-segment-inactive'}`}
                  >
                    Mission
                  </button>
                </div>
              </div>
            </header>

            {/* Progress Indicator */}
            {completedSteps.length > 0 && (
              <ProgressIndicator
                currentStep={currentStep}
                progressPercentage={progressPercentage}
                copy={copy}
              />
            )}

            {/* Hero Section */}
            <HeroSection
              copy={copy}
              onSubmit={onSubmit}
              isLoading={isLoading}
              activeTab={activeTab}
              onTermsClick={handleTermsClick}
            />

            {/* Video Demo Section */}
            <section data-section="video-demo" className="max-w-4xl mx-auto">
              <div className="text-center">
                <VideoDemo />
              </div>
            </section>

            {/* Footer */}
            <Footer
              onTermsClick={handleTermsClick}
              onPrivacyClick={handlePrivacyClick}
            />

            {/* Exit Intent Popup */}
            <ExitIntentPopup
              isVisible={showExitIntent}
              copy={copy}
              onClose={onExitIntentClose}
              onSubmit={onExitIntentSubmit}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default App;
